import { useState, useEffect, useRef } from "react";
import { useMotionValue, useMotionTemplate, useSpring, useTransform } from "framer-motion";
import { motion } from "framer-motion";
//import logoRootWeb from "../images/bleu-fonc-no-bg.png";
import logoLMS from "../images/lmslog.png";
import rapidIcon from "../images/rapide.png";
import designIcon from "../images/designSM.png";
import convertIcon from "../images/penserPC.png";

import servicesIcon from "../images/nosServices.png";
import pourquoiIcon from "../images/pourquoiNC.png";
import notreProc from "../images/notreProc.png";
import pretApasser from "../images/pretPNS.png";

import lmsWAVE from "../images/LMSwave.png";
import bgVideo from "../video/bg-video.mp4";
import { PageMeta } from "../seo/PageMeta";

import { services } from "./data/servicesData";
import { Link } from "react-router-dom";

import Navbar from "./components/navbar";
import Footer from "./components/footer";
import FontLoader from "./components/fontLoader";

// ── Utility: intersection-observer hook ──────────────────────────
function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);

  return [ref, visible];
}

// ── Variants réutilisables ───────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay },
  }),
};

const fadeLeft = {
  hidden: { opacity: 0, x: -32 },
  visible: (delay = 0) => ({
    opacity: 1,
    x: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1], delay },
  }),
};

// ── Hero ─────────────────────────────────────────────────────────
function Hero() {
  const [isContactHovered, setIsContactHovered] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const START_RATE = 6;
    const DURATION_MS = 3000;
    let rafId;
    let startTime;

    const applyIntroEffect = (eased) => {
      video.playbackRate = START_RATE + (1 - START_RATE) * eased;
    };

    const animateIntro = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / DURATION_MS, 1);
      const eased = 1 - (1 - progress) ** 3;
      applyIntroEffect(eased);
      if (progress < 1) rafId = requestAnimationFrame(animateIntro);
    };

    const startRamp = () => {
      applyIntroEffect(0);
      rafId = requestAnimationFrame(animateIntro);
    };

    if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      startRamp();
    } else {
      video.addEventListener("canplay", startRamp, { once: true });
    }

    return () => {
      video.removeEventListener("canplay", startRamp);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
      <video
        ref={videoRef}
        className="absolute inset-0 h-full w-full object-cover"
        src={bgVideo}
        autoPlay
        loop
        muted
        playsInline
        aria-hidden
      />
      {/* Subtitle */}
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 0.7, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
        className="relative z-10 uppercase text-sm md:text-xl"
        style={{color:"#0c1925"}}
      >
        L'agence web qui bâtit votre image
      </motion.p>

      {/* Logo */}
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 flex flex-col items-center gap-1"
      >
        <img src={logoLMS} alt="logo Root Web" className="w-[300px] md:w-[500px] h-auto" />
      </motion.div>

      {/* CTA */}
      <motion.a
        href="/contact"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0, width: isContactHovered ? "28rem" : "13.5rem" }}
        transition={{
          opacity: { duration: 0.5, delay: 0.45, ease: "easeOut" },
          y: { duration: 0.5, delay: 0.45, ease: "easeOut" },
          width: { duration: 1, ease: [0.22, 1, 0.36, 1] },
        }}
        whileHover={{ boxShadow: "0px 8px 30px rgba(12, 25, 37, 0.35)" }}
        onHoverStart={() => setIsContactHovered(true)}
        onHoverEnd={() => setIsContactHovered(false)}
        className="relative z-10 mt-4 px-2 py-3 rounded-full bg-[#0C1925] text-white text-lg font-medium tracking-wide overflow-hidden group inline-flex items-center justify-center whitespace-nowrap"
        style={{ fontFamily: "'Inter', sans-serif", width: "13.5rem" }}
        >
        {/* Shimmer au hover */}
        <motion.span
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full"
            whileHover={{ translateX: "200%" }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
        />
        <span className="relative z-10 flex items-center overflow-hidden">
          <span>Nous contacter</span>
          <motion.span
            className="inline-flex items-center gap-1 overflow-hidden whitespace-nowrap"
            initial={false}
            animate={{
              maxWidth: isContactHovered ? "15rem" : "0rem",
              marginLeft: isContactHovered ? "0.5rem" : "0rem",
            }}
            transition={{
              maxWidth: { duration: 1, ease: [0.22, 1, 0.36, 1] },
              marginLeft: { duration: 1, ease: [0.22, 1, 0.36, 1] },
            }}
          >
            {["pour", "bâtir", "quelque", "chose"].map((word, index) => (
              <motion.span
                key={word}
                className="inline-block"
                initial={false}
                animate={{
                  opacity: isContactHovered ? 1 : 0,
                  y: isContactHovered ? 0 : 20,
                }}
                transition={{
                  opacity: {
                    duration: 0.35,
                    ease: "easeOut",
                    delay: isContactHovered ? 0.2 + index * 0.16 : 0,
                  },
                  y: {
                    duration: 0.5,
                    ease: [0.22, 1, 0.36, 1],
                    delay: isContactHovered ? 0.1 + index * 0.16 : 0,
                  },
                }}
              >
                {word}
              </motion.span>
            ))}
          </motion.span>
        </span>
        </motion.a>

      {/* Wave divider */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0,40 C360,80 1080,0 1440,40 L1440,60 L0,60 Z" fill="white" />
        </svg>
      </div>
    </section>
  );
}

// ── Why us ───────────────────────────────────────────────────────
const reasons = [
  { img: rapidIcon,   body: "Livraison rapide, site rapide. On respecte vos délais sans jamais sacrifier la qualité." },
  { img: designIcon,  body: "Un design exclusif créé autour de vous, pas autour d'un modèle préfabriqué." },
  { img: convertIcon, body: "Un beau site ne suffit pas. On conçoit chaque page pour transformer vos visiteurs en clients." },
];

function ReasonCard({ r, i, visible }) {
    const cardRef = useRef(null);
    const [isHovered, setIsHovered] = useState(false);
  
    const rawX = useMotionValue(-1);
    const rawY = useMotionValue(-1);
  
    // useSpring adoucit les changements de valeur → entrée et sortie fluides
    const mouseX = useSpring(rawX, { stiffness: 80, damping: 20, mass: 0.5 });
    const mouseY = useSpring(rawY, { stiffness: 80, damping: 20, mass: 0.5 });
  
    const handleMouseMove = (e) => {
      const rect = cardRef.current.getBoundingClientRect();
      rawX.set(e.clientX - rect.left);
      rawY.set(e.clientY - rect.top);
    };
  
    const handleMouseEnter = () => {
      setIsHovered(true);
    };
  
    const handleMouseLeave = () => {
      const rect = cardRef.current.getBoundingClientRect();
      // Envoie le point au centre-bas pour une sortie douce vers le bas
      rawX.set(rect.width / 2);
      rawY.set(rect.height * 1.5);
      setTimeout(() => setIsHovered(false), 400); // attend la fin du spring
    };
  
    const background = useMotionTemplate`radial-gradient(
      300px circle at ${mouseX}px ${mouseY}px,
      #4F9ECF,
      #719BBD 40%,
      white 70%
    )`;
  
    return (
      <motion.div
        ref={cardRef}
        initial="hidden"
        animate={visible ? "visible" : "hidden"}
        variants={fadeUp}
        custom={i * 0.12 + 0.1}
        whileHover={{ y: -6, transition: { duration: 0.25 } }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onMouseEnter={handleMouseEnter}
        className="rounded-3xl h-[420px] md:h-auto lg:h-[700px] p-[4px] relative overflow-hidden"
        style={{
          background: isHovered ? undefined : "linear-gradient(to top, #719BBD, white)",
          boxShadow: "0px 4px 10.4px 4px rgba(79, 158, 207, 0.25)",
        }}
      >
        {isHovered && (
          <motion.div
            className="absolute inset-0 rounded-3xl pointer-events-none"
            style={{ background }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          />
        )}
  
        <div className="rounded-[22px] bg-white p-6 md:p-8 flex flex-col md:flex-row lg:flex-col md:items-center gap-4 md:gap-8 h-full md:min-h-[240px] relative">
          <div className="h-[92px] md:h-[140px] lg:h-[92px] md:w-[180px] lg:w-auto flex items-center justify-center mb-4 md:mb-0 lg:mb-12 shrink-0">
            <img src={r.img} alt={r.body} className="max-h-full w-auto max-w-[220px] md:max-w-[180px] lg:max-w-[330px] object-contain" />
          </div>
          <p
            className="text-2xl md:text-2xl lg:text-3xl text-[#1a1a2e]/60 leading-relaxed"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {r.body}
          </p>
        </div>
      </motion.div>
    );
  }
  
  function WhyUs() {
    const [ref, visible] = useReveal();

    return (
      <section
        ref={ref}
        className="py-24 px-6"
        style={{
          background:
            "linear-gradient(180deg,rgb(255, 255, 255) 0%,rgb(233, 245, 255) 50%, #ACD7FF 100%)",
        }}
      >
        <div className="flex justify-center pb-24">
            <CurvedTitle src={pourquoiIcon} alt="Pourquoi nous choisir" width={600} height={600} />
        </div>
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-10">
          {reasons.map((r, i) => (
            <ReasonCard key={r.img} r={r} i={i} visible={visible} />
          ))}
        </div>
      </section>
    );
  }

// ── Services ─────────────────────────────────────────────────────
function Services() {
    const [ref, visible] = useReveal();
    const [hoveredServiceIndex, setHoveredServiceIndex] = useState(null);
  
    return (
      <section
        ref={ref}
        className="py-14 md:py-24 px-6"
      >
        <div className="flex justify-center mb-10 md:mb-24">
            <div className="[&_img]:max-w-[min(260px,88vw)] [&_img]:w-full [&_img]:h-auto [&_img]:object-contain md:[&_img]:max-w-[400px] md:[&_img]:w-auto md:[&_img]:h-auto">
              <CurvedTitle src={servicesIcon} alt="Nos Services" width={400} height={400} />
            </div>
        </div>
        
        <div className="max-w-7xl mx-auto flex flex-col divide-y divide-[#1a1a2e]/10">
        {services.map((service, i) => (
            // Div externe : gère le reveal au scroll
            <Link key={service.id} to={`/infoService/${service.id}`} state={service}>
            <motion.div
                key={service.title}
                initial="hidden"
                animate={visible ? "visible" : "hidden"}
                variants={fadeLeft}
                custom={i * 0.1}
                className="relative py-4 md:py-10 px-4 md:px-6 rounded-2xl overflow-hidden"
            >
                <motion.div
                    className="absolute inset-1 rounded-[20px] pointer-events-none blur-[6px]"
                    style={{
                      background:
                        "linear-gradient(90deg, rgba(172, 215, 255, 0) 0%, rgba(233, 245, 255, 0.5) 10%, rgba(172, 215, 255, 0.82) 50%, rgba(233, 245, 255, 0.5) 90%, rgba(172, 215, 255, 0) 100%)",
                    }}
                    initial={false}
                    animate={{ opacity: hoveredServiceIndex === i ? 1 : 0 }}
                    transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                />
                {/* Div interne : gère le hover indépendamment */}
                <motion.div
                    className="relative z-10 flex flex-col lg:flex-row lg:items-center"
                    initial="rest"
                    whileHover="hover"
                    animate="rest"
                    onHoverStart={() => setHoveredServiceIndex(i)}
                    onHoverEnd={() => setHoveredServiceIndex(null)}
                    >
                    <motion.span
                        className="font-medium whitespace-normal lg:whitespace-nowrap break-words flex-shrink-0 cursor-pointer"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                        variants={{
                        rest: { x: 0, fontSize: "1.875rem", color: "rgba(26,26,46,0.7)" },
                        hover: { x: 20, fontSize: "2.5rem", color: "rgba(26,26,46,1)" },
                        }}
                        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    >
                        {service.title}
                    </motion.span>

                    <motion.p
                        className="mt-3 lg:hidden text-base text-[#1a1a2e]/65 max-w-xl"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={visible ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                        transition={{
                          duration: 0.5,
                          ease: [0.22, 1, 0.36, 1],
                          delay: visible ? 0.45 + i * 0.07 : 0,
                        }}
                    >
                        {service.description}
                    </motion.p>

                    <motion.p
                        className="hidden lg:block text-base text-[#1a1a2e]/60 max-w-xl ml-20"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                        variants={{
                        rest: { opacity: 0, x: -60 },
                        hover: { opacity: 1, x: 0 },
                        }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    >
                        {service.description}
                    </motion.p>
                    </motion.div>
            </motion.div>
            </Link>
            ))}
        </div>
      </section>
    );
  }

const pathSteps = [
  {
    step: "01",
    title: "Prise de rendez-vous",
    description:
      "On se rencontre pour discuter de votre projet, vos besoins et vos objectifs. C'est gratuit et sans engagement.",
  },
  {
    step: "02",
    title: "Développement",
    description:
      "On conçoit votre solution sur mesure en gardant contact avec vous tout au long du processus.",
  },
  {
    step: "03",
    title: "Livraison",
    description:
      "On vous remet un produit fini, testé et prêt à l'emploi. On s'assure que vous êtes 100% satisfait.",
  },
];

function PathSection() {
  const [ref, visible] = useReveal();

  return (
    <section ref={ref} className="py-14 md:py-16 lg:py-20 px-4 sm:px-5 md:px-6 bg-white overflow-x-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          animate={visible ? "visible" : "hidden"}
          variants={fadeUp}
          className="flex justify-center"
        >
          <img
            src={notreProc}
            alt="Notre processus simple en 3 étapes"
            className="w-full mb-5 max-w-[620px] md:max-w-[700px] lg:max-w-[780px] h-auto"
          />
        </motion.div>

        <motion.p
          initial="hidden"
          animate={visible ? "visible" : "hidden"}
          variants={fadeUp}
          custom={0.08}
          className="mt-3 md:mt-5 text-center text-base sm:text-lg md:text-xl text-[#1a1a2e]/65"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          De l&apos;idée à la livraison, on vous accompagne à chaque étape.
        </motion.p>

        <div className="mt-10 md:mt-14 lg:mt-16">
          <div className="relative max-w-6xl mx-auto">
            <div className="absolute left-1/2 -top-4 -bottom-4 md:-top-8 md:-bottom-8 -translate-x-1/2 w-[2px] bg-[#1a1a2e]/15" />

            <div className="space-y-10 sm:space-y-12 md:space-y-16 lg:space-y-24">
              {pathSteps.map((item, index) => {
                const isLeft = index % 2 === 0;
                return (
                  <motion.article
                    key={item.step}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, amount: 0.4 }}
                    variants={{
                      hidden: { opacity: 0, y: 55, x: isLeft ? -45 : 45 },
                      visible: { opacity: 1, y: 0, x: 0 },
                    }}
                    transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
                    className="relative grid grid-cols-1 md:grid-cols-2"
                  >
                    <div className={`${isLeft ? "md:col-start-1 md:mr-16 md:text-right" : "md:col-start-2 md:ml-16 md:text-left"} relative text-center px-1 sm:px-2 md:px-0`}>
                      <motion.div
                        className="absolute md:hidden -inset-x-3 -inset-y-3 rounded-[2rem] pointer-events-none blur-[12px]"
                        style={{
                          background:
                            "radial-gradient(circle at center, rgba(172, 215, 255, 0.75) 0%, rgba(206, 232, 252, 0.45) 35%, rgba(255, 255, 255, 0.12) 70%, rgba(255, 255, 255, 0) 100%)",
                        }}
                        variants={{
                          hidden: { opacity: 0 },
                          visible: { opacity: 1 },
                        }}
                        transition={{ duration: 0.7, delay: 0.28, ease: [0.22, 1, 0.36, 1] }}
                      />
                      <motion.div
                        className="hidden md:block absolute md:-inset-x-12 md:-inset-y-6 rounded-[2rem] pointer-events-none md:blur-[20px]"
                        style={{
                          background: isLeft
                            ? "linear-gradient(270deg, rgba(172, 215, 255, 0.78) 0%, rgba(206, 232, 252, 0.48) 38%, rgba(255, 255, 255, 0.14) 72%, rgba(255, 255, 255, 0) 100%)"
                            : "linear-gradient(90deg, rgba(172, 215, 255, 0.78) 0%, rgba(206, 232, 252, 0.48) 38%, rgba(255, 255, 255, 0.14) 72%, rgba(255, 255, 255, 0) 100%)",
                        }}
                        variants={{
                          hidden: { opacity: 0 },
                          visible: { opacity: 1 },
                        }}
                        transition={{ duration: 0.7, delay: 0.28, ease: [0.22, 1, 0.36, 1] }}
                      />
                      <p
                        className="relative z-10 text-[11px] sm:text-xs md:text-sm tracking-[0.18em] sm:tracking-[0.22em] md:tracking-[0.25em] text-[#4F9ECF] font-semibold"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        ÉTAPE {item.step}
                      </p>
                      <h3
                        className="relative z-10 mt-2 md:mt-3 text-xl sm:text-2xl text-[#1a1a2e]"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        {item.title}
                      </h3>
                      <p
                        className="relative z-10 mt-3 md:mt-5 text-sm sm:text-[15px] md:text-base leading-relaxed text-[#1a1a2e]/70"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        {item.description}
                      </p>
                    </div>

                    <div className="hidden md:block absolute left-1/2 top-3 -translate-x-1/2 z-10 h-5 w-5 rounded-full border-4 border-white bg-[#4F9ECF] shadow-[0_0_0_6px_rgba(79,158,207,0.18)]" />
                  </motion.article>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── CTA ──────────────────────────────────────────────────────────
function CTA() {
  const [ref, visible] = useReveal();
  const [isProjectHovered, setIsProjectHovered] = useState(false);

  return (
    <section
        ref={ref}
        id="contact"
        className="relative py-52 px-6 overflow-hidden"
        style={{ background: "linear-gradient(180deg, #0C1925 0%, #172D41 100%)" }}
        >
        {/* Background scrolling */}
        <ScrollingBackground />

        <div className="relative z-10 mx-auto text-center flex flex-col items-center gap-12">
            <div className="flex justify-center">
            <CurvedTitle src={pretApasser} alt="Prêt à passer?" width={900} height={900} />
            </div>

            <motion.p
            initial="hidden"
            animate={visible ? "visible" : "hidden"}
            variants={fadeUp}
            custom={0.15}
            className="text-xl text-white/50 tracking-wide"
            style={{ fontFamily: "'Inter', sans-serif" }}
            >
            Discutons de votre projet
            </motion.p>

            <motion.div
              initial="hidden"
              animate={visible ? "visible" : "hidden"}
              variants={fadeUp}
              custom={0.25}
            >
            <motion.a
              href="/contact"
              onHoverStart={() => setIsProjectHovered(true)}
              onHoverEnd={() => setIsProjectHovered(false)}
              whileHover={{ backgroundColor: "#EEF6FF", color: "#0C1925", transition: "ease", duration:"0.3s", boxShadow: "0px 10px 28px rgba(172, 215, 255, 0.35)" }}
              transition={{
                width: { duration: 1, ease: [0.22, 1, 0.36, 1] },
                default: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
              }}
              className="mt-2 px-6 py-3 rounded-full bg-white text-[#0C1925] text-lg font-medium tracking-wide overflow-hidden relative inline-flex items-center justify-center whitespace-nowrap group"
              style={{ fontFamily: "'Inter', sans-serif", width: "15rem" }}
              animate={{
                width: isProjectHovered ? "25rem" : "15rem",
              }}
            >
              <motion.span
                className="absolute inset-0 bg-gradient-to-r from-transparent via-[#0C1925]/10 to-transparent -translate-x-full"
                whileHover={{ translateX: "200%" }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
              />
              <span className="relative z-10 flex items-center overflow-hidden">
                <span>Démarrer un projet</span>
                <motion.span
                  className="inline-flex items-center gap-1 overflow-hidden whitespace-nowrap"
                  initial={false}
                  animate={{
                    maxWidth: isProjectHovered ? "15rem" : "0rem",
                    marginLeft: isProjectHovered ? "0.5rem" : "0rem",
                  }}
                  transition={{
                    maxWidth: { duration: 1, ease: [0.22, 1, 0.36, 1] },
                    marginLeft: { duration: 1, ease: [0.22, 1, 0.36, 1] },
                  }}
                >
                  {["qui", "vous", "ressemble"].map((word, index) => (
                    <motion.span
                      key={word}
                      className="inline-block"
                      initial={false}
                      animate={{
                        opacity: isProjectHovered ? 1 : 0,
                        y: isProjectHovered ? 0 : 20,
                      }}
                      transition={{
                        opacity: {
                          duration: 0.35,
                          ease: "easeOut",
                          delay: isProjectHovered ? 0.2 + index * 0.16 : 0,
                        },
                        y: {
                          duration: 0.5,
                          ease: [0.22, 1, 0.36, 1],
                          delay: isProjectHovered ? 0.1 + index * 0.16 : 0,
                        },
                      }}
                    >
                      {word}
                    </motion.span>
                  ))}
                </motion.span>
              </span>
            </motion.a>
            </motion.div>
        </div>
    </section>
  );
}

function CurvedTitle({ src, alt, width, height }) {
    const x = useMotionValue(0);
    const y = useMotionValue(0);
  
    const rotateX = useSpring(useTransform(y, [-300, 300], [6, -6]), { stiffness: 100, damping: 20 });
    const rotateY = useSpring(useTransform(x, [-300, 300], [-6, 6]), { stiffness: 100, damping: 20 });
  
    const handleMouseMove = (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      x.set(e.clientX - rect.left - rect.width / 2);
      y.set(e.clientY - rect.top - rect.height / 2);
    };
  
    return (
      <motion.div
        onMouseMove={handleMouseMove}
        onMouseLeave={() => { x.set(0); y.set(0); }}
        style={{ perspective: 800, display: "inline-block", cursor: "default" }}
      >
        <motion.img
          src={src}
          alt={alt}
          width={width}
          height={height}
          style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        />
      </motion.div>
    );
  }

function ScrollingBackground() {
  const rows = [
    { angle: -40, direction: 1,  duration: 60, y: "-20%" },
    { angle: -40, direction: -1, duration: 75, y: "0%"   },
    { angle: -40, direction: 1,  duration: 55, y: "20%"  },
    { angle: -40, direction: -1, duration: 70, y: "40%"  },
    { angle: -40, direction: 1,  duration: 65, y: "60%"  },
    { angle: -40, direction: -1, duration: 80, y: "80%"  },
  ];

  // Assez d'images pour couvrir toute la largeur même en diagonale
  const images = [...Array(15)];

  return (
    <div className="absolute inset-0 overflow-hidden opacity-10 pointer-events-none">
      {rows.map((row, i) => (
        <div
          key={i}
          className="absolute"
          style={{
            top: row.y,
            left: "-100%",
            right: "-100%",
            transform: `rotate(${row.angle}deg)`,
          }}
        >
          {/* Le trick : on anime translateX de 0 à -50% sur un contenu x2 */}
          <motion.div
            className="flex gap-4"
            animate={{ x: row.direction === 1 ? ["0%", "-50%"] : ["-50%", "0%"] }}
            transition={{
              duration: row.duration,
              repeat: Infinity,
              ease: "linear",
              repeatType: "loop",
            }}
            style={{ width: "max-content" }}
          >
            {/* Bloc A */}
            <div className="flex gap-4 flex-shrink-0">
              {images.map((_, j) => (
                <img key={`a-${j}`} src={lmsWAVE} alt="" className="h-14 w-auto flex-shrink-0" />
              ))}
            </div>
            {/* Bloc B — identique à A pour le loop parfait */}
            <div className="flex gap-4 flex-shrink-0">
              {images.map((_, j) => (
                <img key={`b-${j}`} src={lmsWAVE} alt="" className="h-14 w-auto flex-shrink-0" />
              ))}
            </div>
          </motion.div>
        </div>
      ))}
    </div>
  );
}



// ── Page ─────────────────────────────────────────────────────────
export default function Home() {
  return (
    <>
      <PageMeta
        title="Agence web au Québec"
        description="Agence web à La Tuque spécialisée en sites web rapides, modernes et sur mesure. Développement efficace, design innovant et résultats concrets pour les PME de la Mauricie."
        path="/"
      />
      <FontLoader />
      <Navbar />
      <main style={{ fontFamily: "'Inter', sans-serif" }}>
        <Hero />
        <Services />
        <WhyUs />
        <PathSection />
        <CTA />
        <Footer />
      </main>
    </>
  );
}